import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { AuthProvider } from '../auth/AuthProvider';
import { TransportError } from '../lib/errors';

/**
 * The application's providers — T007.
 *
 * TanStack Query owns server state for every panel (`ARCHITECTURE.md` § 15: loading / empty / error
 * / refetch across ten panels). Its defaults are set once, here, because a retry policy is a product
 * decision rather than a per-screen one, and because the eight stable error codes make that decision
 * unambiguous.
 */

/**
 * Retry only what retrying can fix.
 *
 * `FORBIDDEN`, `UNAUTHENTICATED`, `NOT_FOUND`, `INVALID_ARGUMENT`, `FAILED_PRECONDITION` and
 * `CONFLICT` are decisions about *this* request: repeating it produces the same answer, more slowly,
 * and — for `RATE_LIMITED` — makes the situation worse. `INTERNAL` is the only code that can mean a
 * transient failure (an unreachable backend arrives as `INTERNAL` from `decodeCallableError`), so it
 * is the only one retried, once.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (!(error instanceof TransportError)) return false;
  return error.code === 'INTERNAL' && failureCount < 1;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Panels are read repeatedly while an administrator works through a list; half a minute of
        // freshness removes the refetch storm without ever showing yesterday's data.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      // A mutation is a write. Repeating one automatically is how a single approval becomes two.
      mutations: { retry: false },
    },
  });
}

export function AppProviders({ children }: { readonly children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
